import React, { useState, useEffect, useCallback } from 'react'
import { config } from 'hungry-core2'

import { FlexContainer, YSpacing } from '@components/common'
import { AutocompleteInput } from '@components/common/form'
import { startCase } from 'lodash'

import { validateHubspotUsers } from '@utils/hubspot'
import {
  ShowInfoModalReq,
  ChangeHubspotCompanyReq,
  SearchHubspotCompanyByIdReq,
  FullHubspotCompany,
} from '@types'

interface HubspotLinkProps {
  account: { id: string }
  hubspotCompany: {
    id: string
    name: string
  }

  width?: string
  changeHubspotCompany: (
    req: ChangeHubspotCompanyReq,
  ) => Promise<FullHubspotCompany | null>
  showChangeCompanyModal: (req: ShowInfoModalReq) => void
  dismissChangeCompanyModal: () => void
  searchHubspotCompanyById: (
    req: SearchHubspotCompanyByIdReq,
  ) => Promise<FullHubspotCompany[] | null>
  getHubspotCompanyById: (req: string) => Promise<FullHubspotCompany | null>
  checkCompanyLinked: (req: string) => Promise<boolean>
  flashError: (message: string) => void
  flashSuccess: (message: string) => void
  flashWarning: (message: string) => void
}

const HubspotLink: React.FC<HubspotLinkProps> = ({
  account,
  hubspotCompany,
  width = '31%',
  showChangeCompanyModal,
  dismissChangeCompanyModal,
  changeHubspotCompany,
  searchHubspotCompanyById,
  getHubspotCompanyById,
  checkCompanyLinked,
  flashError,
  flashSuccess,
  flashWarning,
}) => {
  const onChangeCompany = () => {
    showChangeCompanyModal({
      title: 'Change Hubspot Company',
      content: (
        <ChangeHubspotCompanyModal
          account={account}
          hubspotCompany={hubspotCompany}
          changeHubspotCompany={changeHubspotCompany}
          searchHubspotCompanyById={searchHubspotCompanyById}
          getHubspotCompanyById={getHubspotCompanyById}
          checkCompanyLinked={checkCompanyLinked}
          flashError={flashError}
          flashSuccess={flashSuccess}
          flashWarning={flashWarning}
          dismissChangeCompanyModal={dismissChangeCompanyModal}
        />
      ),
    })
  }

  return (
    <FlexContainer width={width} flexDirection="column">
      <a
        className="order-link"
        href={`https://${config.hubspot_url_prefix}/company/${hubspotCompany.id}`}
        target="_blank"
        rel="noopener noreferrer"
      >
        {hubspotCompany.name}
      </a>
      {account.id && account.id !== '' && (
        <button
          onClick={onChangeCompany}
          className="text-xs rounded-md bg-blue-500 text-white px-2 py-1 mt-2"
        >
          Change Company
        </button>
      )}
    </FlexContainer>
  )
}

export default HubspotLink

interface ChangeHubspotCompanyModalProps {
  account: { id: string }
  hubspotCompany: {
    id: string
    name: string
  }
  changeHubspotCompany: (
    req: ChangeHubspotCompanyReq,
  ) => Promise<FullHubspotCompany | null>
  searchHubspotCompanyById: (
    req: SearchHubspotCompanyByIdReq,
  ) => Promise<FullHubspotCompany[] | null>
  getHubspotCompanyById: (
    id: string,
    params?: Record<string, any>,
  ) => Promise<FullHubspotCompany | null>
  checkCompanyLinked: (req: string) => Promise<boolean>
  flashError: (message: string) => void
  flashSuccess: (message: string) => void
  flashWarning: (message: string) => void
  dismissChangeCompanyModal: () => void
}
const ChangeHubspotCompanyModal: React.FC<ChangeHubspotCompanyModalProps> = ({
  account,
  hubspotCompany,
  changeHubspotCompany,
  searchHubspotCompanyById,
  getHubspotCompanyById,
  checkCompanyLinked,
  flashError,
  flashSuccess,
  flashWarning,
  dismissChangeCompanyModal,
}) => {
  const [currentHubspotCompany, setCurrentHubspotCompany] =
    useState<FullHubspotCompany | null>(null)
  const [newHubspotCompany, setNewHubspotCompany] =
    useState<FullHubspotCompany | null>(null)
  const [companyError, setCompanyError] = useState<boolean>(false)

  const getCurrentHubspotCompany = useCallback(async () => {
    const company = await getHubspotCompanyById(hubspotCompany.id, {
      skipValidation: true,
    })
    if (company) {
      setCurrentHubspotCompany(company)
    }
  }, [hubspotCompany.id, getHubspotCompanyById])

  useEffect(() => {
    getCurrentHubspotCompany()
  }, [getCurrentHubspotCompany])

  const onSelectHubspotCompany = async (company: FullHubspotCompany) => {
    setCompanyError(false)
    const isLinked = await checkCompanyLinked(company.id)
    if (isLinked) {
      flashError(
        'The selected hubspot company is already linked to another HUNGRY account',
      )

      return
    }
    const error = validateHubspotUsers(company, flashError, flashWarning)
    setCompanyError(error)
    setNewHubspotCompany(company)
  }

  const onSave = async () => {
    if (!newHubspotCompany) {
      return
    }
    const newCompany = await changeHubspotCompany({
      hungryId: account.id,
      hubspotId: newHubspotCompany.id,
    })
    if (newCompany) {
      setCurrentHubspotCompany(newCompany)
      setNewHubspotCompany(null)
      flashSuccess(
        'HubSpot company successfully changed. Please allow up to 5 minutes for the change to take effect.',
      )
      dismissChangeCompanyModal()
    }
  }

  const renderHubspotCompany = (company: FullHubspotCompany) => {
    const hiddenFields = [
      'id',
      'userId',
      'hubspotOwnerId',
      'contacts',
      'hungryUserId',
    ]

    const displayValue = (value: any) => {
      if (!value) {
        return 'N/A'
      }
      if (typeof value === 'object') {
        return Object.entries(value)
          .filter(([key]) => !hiddenFields.includes(key))
          .map(([key, value]) => (
            <div key={key} className="mb-2 ml-5">
              <span className="font-bold">{startCase(key)}:</span>{' '}
              {displayValue(value)}
            </div>
          ))
      }

      return value.toString()
    }

    return Object.entries(company || {})
      .filter(([key]) => !hiddenFields.includes(key))
      .sort(([keyA], [keyB]) => keyA.localeCompare(keyB))
      .map(([key, value]) => (
        <div key={key} className="mb-2">
          <span className="font-bold">{startCase(key)}:</span>{' '}
          {displayValue(value)}
        </div>
      ))
  }

  return (
    <FlexContainer flexDirection="column" width="100%" height="80vh">
      <FlexContainer
        flexDirection="row"
        width="100%"
        justifyContent="space-between"
      >
        <AutocompleteInput
          label="Select New HubSpot Company"
          displayAttributes={['name', 'city', 'state', 'domain']}
          value={newHubspotCompany && newHubspotCompany.name}
          includePagination={false}
          searchDelay={2000}
          refreshOnSelect={false}
          loaderFunction={searchHubspotCompanyById}
          onSelect={onSelectHubspotCompany}
        />
        {newHubspotCompany && (
          <button
            onClick={onSave}
            disabled={companyError}
            className="text-xs rounded-md bg-red-500 text-white px-2 py-1 mt-2"
          >
            Update Company
          </button>
        )}
      </FlexContainer>
      <YSpacing height="10px" />
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: '1fr 1fr',
          padding: '5px',
        }}
        className="w-full"
      >
        {currentHubspotCompany && (
          <div style={{ gridColumn: 1 }} className="w-full">
            <h2 className="text-lg font-bold mb-2">Current HubSpot Company</h2>
            {renderHubspotCompany(currentHubspotCompany)}
          </div>
        )}
        {newHubspotCompany && (
          <div style={{ gridColumn: 2 }} className="w-full">
            <h2 className="text-lg font-bold mb-2">New HubSpot Company</h2>
            {renderHubspotCompany(newHubspotCompany)}
          </div>
        )}
      </div>
    </FlexContainer>
  )
}
